import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { Wallet, Receipt, Percent, Landmark, CalendarClock, ArrowRight } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { useCompany } from "@/context/CompanyContext";
import { useTheme } from "@/context/ThemeContext";
import { fmtEur, fmtDate, BRACKET_COLORS, STATUS_LABELS, STATUS_CLASSES } from "@/lib/format";
import { TaxBreakdown } from "@/components/TaxBreakdown";
import { ExerciseCompare } from "@/components/ExerciseCompare";
import { Button } from "@/components/ui/button";

const Kpi = ({ label, value, sub, icon: Icon, accent, testId }) => (
  <div className="kpi-card fade-up" style={{ "--kpi-accent": accent }}>
    <div className="flex items-start justify-between">
      <div className="eyebrow">{label}</div>
      <Icon className="h-4 w-4 text-muted-foreground/70" />
    </div>
    <div className="font-mono text-2xl font-semibold text-foreground mt-3" data-testid={testId}>{value}</div>
    {sub && <div className="text-xs text-muted-foreground mt-1">{sub}</div>}
  </div>
);

export default function DashboardPage() {
  const { company, companyId, year, loaded } = useCompany();
  const { isDark } = useTheme();
  const [data, setData] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!companyId) return;
    setData(null);
    api.get(`/companies/${companyId}/summary`, { params: { annee: year } }).then((r) => setData(r.data)).catch((e) => toast.error(apiError(e)));
  }, [companyId, year]);

  if (loaded && !company) {
    return (
      <div className="bg-card border border-border rounded-md p-12 text-center fade-up" data-testid="dashboard-empty">
        <Landmark className="h-10 w-10 mx-auto text-brand-500" />
        <h1 className="font-heading text-2xl font-bold mt-4">Commencez par créer votre première entreprise</h1>
        <p className="text-muted-foreground mt-2 max-w-md mx-auto">Renseignez la raison sociale, le SIRET, le rapport d'assujettissement et le statut associatif pour calculer la taxe sur les salaires.</p>
        <Button className="mt-6" onClick={() => navigate("/parametres?new=1")} data-testid="dashboard-create-company-button">Créer une entreprise <ArrowRight className="h-4 w-4 ml-2" /></Button>
      </div>
    );
  }
  if (!data) return <div className="text-muted-foreground" data-testid="dashboard-loading">Chargement du tableau de bord…</div>;

  const a = data.annuel;
  const allegements = a.abattement_association + a.franchise + a.decote;
  const today = new Date().toISOString().slice(0, 10);
  const nextEch = data.echeances.filter((e) => e.date >= today).slice(0, 4);

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div className="flex items-end justify-between">
        <div>
          <div className="eyebrow">Synthèse fiscale · exercice {year}</div>
          <h1 className="font-heading text-3xl font-extrabold tracking-tight text-foreground mt-1">Tableau de bord</h1>
        </div>
        <div className="text-sm text-muted-foreground">
          Périodicité : <span className="font-medium text-foreground">{data.periodicite}</span>
          {data.periodicite_recommandee !== data.periodicite && <span className="ml-2 text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10 border border-amber-200 dark:border-amber-500/40 rounded px-2 py-0.5 text-xs" data-testid="periodicite-warning">recommandée : {data.periodicite_recommandee}</span>}
        </div>
      </div>

      <div className="grid md:grid-cols-2 xl:grid-cols-4 gap-4">
        <Kpi label="Masse salariale brute" value={fmtEur(a.masse_salariale)} sub={`${a.effectif} salarié(s)`} icon={Wallet} accent={isDark ? "#9488DC" : "#141226"} testId="kpi-gross-payroll" />
        <Kpi label="Taxe brute" value={fmtEur(a.taxe_brute)} sub="avant allègements" icon={Receipt} accent="#7161D0" testId="kpi-tax-gross" />
        <Kpi label="Allègements" value={fmtEur(allegements)} sub="abattement · franchise · décote" icon={Percent} accent="#D97706" testId="kpi-abatement-amount" />
        <Kpi label="Taxe nette due" value={fmtEur(a.taxe_nette)} sub={`Solde ${year} (2502-SD)`} icon={Landmark} accent="#059669" testId="kpi-tax-net-due" />
      </div>

      <div className="grid xl:grid-cols-[1.4fr_1fr] gap-6">
        <div className="bg-card border border-border rounded-md p-5">
          <div className="eyebrow mb-4">Taxe brute mensuelle par tranche</div>
          <div className="h-64 min-h-[256px] w-full" data-testid="monthly-chart">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={data.mensuel} margin={{ left: 0, right: 0, top: 4, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={isDark ? "#2A2650" : "#E2E8F0"} />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: isDark ? "#9A95B8" : "#64748B" }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: isDark ? "#9A95B8" : "#64748B" }} axisLine={false} tickLine={false} width={64} tickFormatter={(v) => `${Math.round(v)} €`} />
                <Tooltip formatter={(v, n) => [fmtEur(v), { taxe1: "4,25 %", taxe2: "8,50 %", taxe3: "13,60 %" }[n]]} cursor={{ fill: isDark ? "#201C42" : "#F1F5F9" }} contentStyle={isDark ? { background: "#171433", border: "1px solid #2A2650", color: "#F1EFFA" } : undefined} />
                <Bar dataKey="taxe1" stackId="a" fill={BRACKET_COLORS[0]} />
                <Bar dataKey="taxe2" stackId="a" fill={BRACKET_COLORS[1]} />
                <Bar dataKey="taxe3" stackId="a" fill={BRACKET_COLORS[2]} radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="bg-card border border-border rounded-md p-5">
          <div className="eyebrow mb-4 flex items-center gap-2"><CalendarClock className="h-4 w-4" /> Prochaines échéances</div>
          <ul className="space-y-3" data-testid="deadlines-list">
            {nextEch.length === 0 && <li className="text-sm text-muted-foreground">Aucune échéance à venir pour {year}.</li>}
            {nextEch.map((e) => (
              <li key={`${e.type}-${e.periode}`} className="flex items-center justify-between text-sm border-b border-border/60 pb-2 last:border-0">
                <div>
                  <div className="font-medium text-foreground">{e.libelle}</div>
                  <div className="text-xs text-muted-foreground font-mono">{e.formulaire}</div>
                </div>
                <div className="font-mono text-foreground/80">{fmtDate(e.date)}</div>
              </li>
            ))}
          </ul>
          <div className="mt-5 pt-4 border-t border-border">
            <div className="eyebrow mb-2">Déclarations {year}</div>
            {data.declarations.length === 0 ? <p className="text-sm text-muted-foreground">Aucune déclaration. <Link to="/declarations" className="text-brand-600 dark:text-brand-300 hover:underline">Créer</Link></p> : (
              <ul className="space-y-1.5">
                {data.declarations.slice(0, 4).map((d) => (
                  <li key={d.id} className="flex items-center justify-between text-sm">
                    <Link to={`/declarations/${d.id}`} className="text-foreground hover:text-brand-600 dark:text-brand-300">{d.label}</Link>
                    <span className={`text-[11px] px-2 py-0.5 rounded border ${STATUS_CLASSES[d.status]}`}>{STATUS_LABELS[d.status]}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      <ExerciseCompare companyId={companyId} year={year} />

      <div>
        <div className="eyebrow mb-3">Liquidation annuelle estimée</div>
        <TaxBreakdown result={a} />
      </div>
    </div>
  );
}
